import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import * as auth from "../services/auth";

export default function AccountBilling() {
  const navigate = useNavigate();
  const { subscriptions = [], refreshSession } = useAuth();

  const [loading, setLoading] = useState(false);
  const [actionType, setActionType] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [localSubscription, setLocalSubscription] = useState(null);

  const activeSubscription = useMemo(() => {
    if (!Array.isArray(subscriptions)) return null;

    const now = new Date();

    return subscriptions.find((subscription) => {
      const status = getSubscriptionStatus(subscription);
      const accessStatus = subscription.access_status;
      const endDate = getEndDate(subscription);
      const hasFutureAccess = endDate ? new Date(endDate) > now : false;

      if (isCancelScheduled(subscription) && hasFutureAccess) return true;
      if (accessStatus === "trial_canceling" || accessStatus === "active_canceling") return true;
      if (status === "active" || status === "past_due") return true;
      if (status === "trialing") return subscription.trial_end ? hasFutureAccess : true;

      return false;
    });
  }, [subscriptions]);

  const visibleSubscription = localSubscription || activeSubscription;
  const cancelScheduled = isCancelScheduled(visibleSubscription);
  const statusLabel = getStatusLabel(visibleSubscription);
  const startDate = getStartDate(visibleSubscription);
  const endDate = getEndDate(visibleSubscription);
  const statusDescription = getStatusDescription(visibleSubscription);
  const feedbackMessage = error || message;

  function getSubscriptionStatus(subscription) {
    if (!subscription) return "";

    return subscription.status || subscription.membership_status || "";
  }

  function isCancelScheduled(subscription) {
    if (!subscription) return false;

    return (
      subscription.cancel_at_period_end === true ||
      subscription.cancel_at_period_end === "true" ||
      subscription.access_status === "trial_canceling" ||
      subscription.access_status === "active_canceling"
    );
  }

  function formatDate(dateValue) {
    if (!dateValue) return "—";

    try {
      return new Date(dateValue).toLocaleDateString("es-CA", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  }

  function getEndDate(subscription) {
    if (!subscription) return null;

    return (
      subscription.access_end ||
      subscription.current_period_end ||
      subscription.trial_end ||
      subscription.expires_at ||
      null
    );
  }

  function getStartDate(subscription) {
    if (!subscription) return null;

    return (
      subscription.current_period_start ||
      subscription.trial_start ||
      subscription.created_at ||
      null
    );
  }

  function getStatusLabel(subscription) {
    if (!subscription) return "Sin membresía activa";

    const status = getSubscriptionStatus(subscription);

    if (isCancelScheduled(subscription)) {
      if (status === "trialing") return "Prueba cancelada";
      return "Cancelación programada";
    }

    if (status === "trialing") return "En prueba gratuita";
    if (status === "active") return "Activa";
    if (status === "past_due") return "Pago pendiente";
    if (status === "canceled") return "Cancelada";

    return "No disponible";
  }

  function getStatusDescription(subscription) {
    if (!subscription) {
      return "No encontramos una membresía activa asociada a tu cuenta.";
    }

    const status = getSubscriptionStatus(subscription);
    const formattedEnd = endDate ? formatDate(endDate) : "la fecha indicada";

    if (isCancelScheduled(subscription)) {
      return `Tu cancelación está programada. Mantendrás acceso hasta el ${formattedEnd}. No se realizará un nuevo cobro mientras la cancelación permanezca activa.`;
    }

    if (status === "trialing") {
      return `Tu prueba gratuita está activa hasta el ${formattedEnd}. Si no cancelas antes de esa fecha, la membresía continuará con renovación automática.`;
    }

    if (status === "active") {
      return "Tu membresía está activa. Si decides cancelar, conservarás acceso hasta el final del período vigente.";
    }

    if (status === "past_due") {
      return "Hay un problema con el pago de tu membresía. Revisa el método de pago para evitar interrupciones de acceso.";
    }

    return "Puedes revisar aquí el estado actual de tu membresía.";
  }

  async function handleCancelMembership() {
    setLoading(true);
    setActionType("cancel");
    setMessage("");
    setError("");

    try {
      const res = await auth.cancelMembership();

      if (!res?.ok) {
        throw new Error(res?.error || "No se pudo cancelar la suscripción.");
      }

      const current = visibleSubscription || activeSubscription || {};
      const currentStatus = getSubscriptionStatus(current);

      const updatedSubscription = {
        ...current,
        ...(res.subscription || {}),
        cancel_at_period_end: true,
        access_status:
          currentStatus === "trialing" ? "trial_canceling" : "active_canceling",
      };

      setLocalSubscription(updatedSubscription);
      await refreshSession();

      // No mostramos mensaje adicional aquí para evitar duplicar
      // el aviso permanente de cancelación programada.
      setMessage("");
    } catch (err) {
      setError(err.message || "No se pudo cancelar la suscripción.");
    } finally {
      setLoading(false);
      setActionType("");
    }
  }

  async function handleReactivateMembership() {
    setLoading(true);
    setActionType("reactivate");
    setMessage("");
    setError("");

    try {
      const res = await auth.reactivateMembership();

      if (!res?.ok) {
        throw new Error(res?.error || "No se pudo reactivar la membresía.");
      }

      const current = visibleSubscription || activeSubscription || {};
      const currentStatus = getSubscriptionStatus(current);
      const nextStatus =
        currentStatus === "trialing"
          ? "trialing"
          : getSubscriptionStatus(res.subscription) || currentStatus || "active";

      const updatedSubscription = {
        ...current,
        ...(res.subscription || {}),
        status: nextStatus,
        cancel_at_period_end: false,
        access_status: nextStatus,
      };

      setLocalSubscription(updatedSubscription);
      await refreshSession();
      setMessage("Membresía reactivada correctamente.");
    } catch (err) {
      setError(err.message || "No se pudo reactivar la membresía.");
    } finally {
      setLoading(false);
      setActionType("");
    }
  }

  return (
    <main style={pageStyle}>
      <button
        type="button"
        onClick={() => navigate("/dashboard")}
        style={backDashboardButtonStyle}
      >
        ← Volver al dashboard
      </button>

      <section style={cardStyle}>
        <div style={headerRowStyle}>
          <div>
            <p style={eyebrowStyle}>Mi membresía</p>
            <h1 style={titleStyle}>Gestionar suscripción</h1>
            <p style={textStyle}>
              Revisa el estado de tu membresía, la fecha de acceso vigente y las acciones disponibles para tu cuenta.
            </p>
          </div>

          <span style={statusPillStyle}>{statusLabel}</span>
        </div>

        <div style={membershipInfoPanelStyle}>
          <div style={accessContentStyle}>
           <div style={accessTextColumnStyle}>
             <span style={accessLabelStyle}>Acceso vigente</span>

             <p style={membershipDescriptionStyle}>{statusDescription}</p>
           </div>

           <div style={datesSidebarStyle}>
             <div style={compactDateCardStyle}>
               <span style={compactDateLabelStyle}>Desde</span>
               <span style={compactDateValueStyle}>{formatDate(startDate)}</span>
             </div>

             <div style={compactDateCardStyle}>
               <span style={compactDateLabelStyle}>Hasta</span>
               <span style={compactDateValueStyle}>{formatDate(endDate)}</span>
             </div>
           </div>
         </div>
       </div>

        {feedbackMessage && (
          <div style={error ? feedbackErrorStyle : feedbackSuccessStyle}>
            {feedbackMessage}
          </div>
        )}

        {visibleSubscription ? (
          cancelScheduled ? (
            <div style={actionPanelStyle}>
              <div style={successBoxStyle}>
                <strong>Cancelación programada.</strong> Mantendrás acceso hasta el{" "}
                {endDate ? formatDate(endDate) : "final del período actual"}. No se realizará un nuevo cobro mientras la cancelación permanezca programada.
              </div>

              <p style={legalNoteStyle}>
                Te enviamos una confirmación por correo electrónico como comprobante de esta solicitud. Puedes revisar los términos aplicables en Términos y Condiciones y Política de Privacidad.
              </p>

              <div style={reactivationBoxStyle}>
                <div>
                  <strong style={reactivationTitleStyle}>¿Deseas continuar?</strong>
                  <p style={reactivationTextStyle}>
                    Puedes reactivar tu membresía antes de la fecha indicada. No se crea una nueva prueba gratuita; simplemente se mantiene activa la suscripción vigente.
                  </p>
                </div>

                <button
                  type="button"
                  style={{
                    ...primaryButtonStyle,
                    opacity: loading && actionType === "reactivate" ? 0.72 : 1,
                  }}
                  onClick={handleReactivateMembership}
                  disabled={loading}
                >
                  {loading && actionType === "reactivate"
                    ? "Reactivando..."
                    : "Reactivar membresía"}
                </button>
              </div>
            </div>
          ) : (
            <div style={actionPanelStyle}>
              <div style={warningStyle}>
                <strong>Antes de cancelar:</strong> tu suscripción no se renovará automáticamente. Mantendrás acceso hasta la fecha indicada. Si ya existe un período mensual pagado, la cancelación no genera reembolso parcial.
              </div>

              <button
                type="button"
                style={{
                  ...dangerButtonStyle,
                  opacity: loading && actionType === "cancel" ? 0.72 : 1,
                }}
                onClick={handleCancelMembership}
                disabled={loading}
              >
                {loading && actionType === "cancel"
                  ? "Cancelando..."
                  : "Confirmar cancelación"}
              </button>
            </div>
          )
        ) : (
          <div style={actionPanelStyle}>
            <p style={emptyTextStyle}>
              No encontramos una membresía activa asociada a tu cuenta.
            </p>

            <button
              type="button"
              style={primaryButtonStyle}
              onClick={() => navigate("/store")}
            >
              Volver a activar membresía
            </button>
          </div>
        )}
      </section>
    </main>
  );
}

const pageStyle = {
  maxWidth: 840,
  margin: "0 auto",
  padding: "4.5rem 1rem 3.5rem",
  fontFamily: "Questrial, sans-serif",
};

const backDashboardButtonStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  gap: 8,
  marginBottom: 22,
  padding: "11px 19px",
  borderRadius: 999,
  border: "1px solid rgba(40, 107, 148, 0.22)",
  background: "rgba(255, 255, 255, 0.82)",
  color: "#286b94",
  fontSize: 15,
  fontWeight: 800,
  lineHeight: 1,
  cursor: "pointer",
  boxShadow: "0 10px 24px rgba(20, 55, 80, 0.08)",
};

const cardStyle = {
  background: "#ffffff",
  borderRadius: 26,
  padding: "2rem",
  boxShadow: "0 18px 45px rgba(12, 35, 52, 0.08)",
  border: "1px solid rgba(15, 47, 70, 0.07)",
};

const headerRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: "1.25rem",
  marginBottom: "1.35rem",
};

const eyebrowStyle = {
  margin: 0,
  color: "#c79a45",
  textTransform: "uppercase",
  letterSpacing: "0.12em",
  fontWeight: 900,
  fontSize: 12,
};

const titleStyle = {
  margin: "0.55rem 0 0",
  fontFamily: "Questrial, sans-serif",
  color: "#0f2f46",
  fontWeight: 400,
  letterSpacing: "-0.03em",
  fontSize: "clamp(1.7rem, 3vw, 2.25rem)",
  lineHeight: 1.05,
};

const textStyle = {
  margin: "0.8rem 0 0",
  color: "#66758a",
  lineHeight: 1.65,
  fontSize: 15,
  maxWidth: 570,
};

const statusPillStyle = {
  flexShrink: 0,
  padding: "0.48rem 0.78rem",
  borderRadius: 999,
  background: "#f7f9fb",
  border: "1px solid #e4ebf1",
  color: "#173d56",
  fontSize: 13,
  fontWeight: 900,
  whiteSpace: "nowrap",
};

const membershipInfoPanelStyle = {
  marginTop: 20,
  padding: "20px 18px",
  borderRadius: 24,
  border: "1px solid rgba(40, 107, 148, 0.13)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.9))",
  boxShadow: "0 16px 38px rgba(20, 55, 80, 0.055)",
};

const accessContentStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 18,
};

const accessTextColumnStyle = {
  flex: 1,
  minWidth: 0,
};

const accessLabelStyle = {
  display: "block",
  marginBottom: 10,
  color: "#c79a45",
  fontSize: 12,
  fontWeight: 900,
  letterSpacing: "0.11em",
  textTransform: "uppercase",
};

const membershipStatusRowStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 14,
  paddingBottom: 13,
  marginBottom: 12,
  borderBottom: "1px solid rgba(40, 107, 148, 0.10)",
};

const membershipLabelStyle = {
  color: "rgba(40, 107, 148, 0.72)",
  fontSize: 13,
  fontWeight: 700,
};

const membershipStatusValueStyle = {
  color: "#286b94",
  fontSize: 15,
  fontWeight: 900,
  textAlign: "right",
};

const membershipDescriptionStyle = {
  margin: 0,
  color: "#5f6f80",
  fontSize: 14,
  lineHeight: 1.65,
  maxWidth: 560,
};

const membershipInfoContentStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "stretch",
  gap: 18,
  flexWrap: "wrap",
};

const membershipMainColumnStyle = {
  flex: "1 1 420px",
  minWidth: 0,
};

const datesSidebarStyle = {
  width: 108,
  display: "flex",
  flexDirection: "column",
  gap: 7,
  flexShrink: 0,
};

const compactDateCardStyle = {
  minHeight: 48,
  padding: "7px 9px",
  borderRadius: 13,
  border: "1px solid rgba(40, 107, 148, 0.12)",
  background:
    "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,250,252,0.9))",
  boxShadow: "0 5px 12px rgba(20, 55, 80, 0.035)",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
};

const compactDateLabelStyle = {
  display: "block",
  marginBottom: 5,
  color: "rgba(40, 107, 148, 0.58)",
  fontSize: 10,
  fontWeight: 900,
  letterSpacing: "0.07em",
  textTransform: "uppercase",
};

const compactDateValueStyle = {
  display: "block",
  color: "#1f5f86",
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.2,
  whiteSpace: "nowrap",
};

const feedbackSuccessStyle = {
  marginTop: "1rem",
  padding: "0.82rem 0.95rem",
  borderRadius: 16,
  background: "#f0fbf5",
  border: "1px solid #c8f3d7",
  color: "#166534",
  fontWeight: 900,
  fontSize: 14,
  lineHeight: 1.55,
};

const feedbackErrorStyle = {
  marginTop: "1rem",
  padding: "0.82rem 0.95rem",
  borderRadius: 16,
  background: "#fff5f5",
  border: "1px solid rgba(180, 35, 24, 0.18)",
  color: "#b42318",
  fontWeight: 900,
  fontSize: 14,
  lineHeight: 1.55,
};

const actionPanelStyle = {
  marginTop: "1rem",
};

const warningStyle = {
  padding: "0.9rem 1rem",
  borderRadius: 16,
  background: "#fffaf1",
  border: "1px solid rgba(199, 154, 69, 0.25)",
  color: "#6b4f1f",
  lineHeight: 1.6,
  fontSize: 14,
};

const successBoxStyle = {
  padding: "0.9rem 1rem",
  borderRadius: 16,
  background: "#f0fbf5",
  border: "1px solid #c8f3d7",
  color: "#166534",
  lineHeight: 1.6,
  fontSize: 14,
};

const legalNoteStyle = {
  margin: "0.8rem 0 0",
  color: "#66758a",
  fontSize: 13,
  lineHeight: 1.6,
};

const reactivationBoxStyle = {
  marginTop: "1rem",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "1rem",
  padding: "1rem",
  borderRadius: 18,
  background: "#f8fafc",
  border: "1px solid #e6edf3",
};

const reactivationTitleStyle = {
  display: "block",
  color: "#0f2f46",
  fontSize: 15,
  marginBottom: 4,
};

const reactivationTextStyle = {
  margin: 0,
  color: "#66758a",
  fontSize: 13,
  lineHeight: 1.55,
};

const dangerButtonStyle = {
  marginTop: "1rem",
  border: "none",
  borderRadius: 999,
  padding: "0.72rem 1rem",
  background: "#173d56",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 900,
  cursor: "pointer",
  boxShadow: "0 12px 26px rgba(23, 61, 86, 0.18)",
};

const primaryButtonStyle = {
  flexShrink: 0,
  border: "none",
  borderRadius: 999,
  padding: "0.72rem 1rem",
  background: "#286b94",
  color: "#ffffff",
  fontSize: 14,
  fontWeight: 900,
  cursor: "pointer",
  boxShadow: "0 12px 26px rgba(40, 107, 148, 0.18)",
};

const emptyTextStyle = {
  margin: "0 0 1rem",
  color: "#66758a",
  fontSize: 15,
  lineHeight: 1.6,
};